import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Drawer, Button } from '@mui/material';
import { useAnalysisStore } from '../../store/useAnalysisStore';
import toast from 'react-hot-toast';
import Url from '../../data/url';
import { useStartVideoAnalysis } from '../../hook/useStartVideoAnalysis';
import IcCheckMintOn from '../../assets/image/ic_check_mint_on.svg';
import IcClose from '../../assets/image/ic_x.svg';
import { ON_KIDS_ANALYSIS_THUMBNAIL, ON_KIDS_ANALYSIS_TYPE } from '../../data/define';
import KidCaracterAddDialog from './KidCaranterAddDialog';
import { useTranslation } from '../../hook/useTranslation';

interface RepImageSelectDialogProps {
  open: boolean;
  onClose?: () => void;
  thumbnails?: string[];
  bboxes?: any[];
  savedVideo?: Blob | File | null;
  kidId?: number | null;
  mi?: number[];
  reportId?: number | null;
}

function RepImageSelectDialog({
  open,
  onClose = () => {},
  thumbnails = [],
  bboxes = [],
  savedVideo = null,
  kidId = null,
  mi = [],
  reportId = null,
}: RepImageSelectDialogProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type');
  const { startAnalysis } = useStartVideoAnalysis();
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [kidCaracterAddDialogOpen, setKidCaracterAddDialogOpen] = useState(false);
  const [inputValue, setInputValue] = useState({
    impression: '',
    physical: '',
    behavior: '',
    isOnlyOne: '',
  });
  const setSavedVideo = useAnalysisStore((s) => s.setSavedVideo);
  const setSavedThumbnail = useAnalysisStore((s) => s.setSavedThumbnail);
  const setAbortController = useAnalysisStore((s) => s.setAbortController); // 영상분석 요청 중 로그아웃시 대기 취소

  const handleStartAnalysis = useCallback(() => {
    if (selectedImageIndex === null) return;
    const selectedThumbnail = thumbnails[selectedImageIndex];
    const selectedThumbnailBBox = bboxes[selectedImageIndex];
    if (!selectedThumbnail) {
      toast.error(t('upload.toast_thumbnail_error'));
      return;
    }
    const targetString = `[tg]${inputValue.impression}[/tg][tg]${inputValue.physical}[/tg][tg]${inputValue.behavior}[/tg][tg]${inputValue.isOnlyOne}[/tg]`;
    setKidCaracterAddDialogOpen(false);

    setSavedVideo(savedVideo); // 비디오파일 저장
    setSavedThumbnail({
      thumbnail: selectedThumbnail,
      origCoords: selectedThumbnailBBox || null,
    }); // 썸네일 저장

    // api pending 중 로그아웃 시점에서 요청 취소
    const controller = new AbortController();
    setAbortController(controller);
    sessionStorage.setItem(ON_KIDS_ANALYSIS_THUMBNAIL, selectedThumbnail);
    localStorage.setItem(ON_KIDS_ANALYSIS_TYPE, type || '');
    startAnalysis(
      selectedThumbnail,
      kidId || 0,
      targetString,
      mi,
      reportId || undefined,
      type || '',
      controller.signal
    ); // 백그라운드 request (fire-and-forget)

    navigate(Url.KidsAnalysisResult, {
      state: {
        kidId: kidId,
        mi: mi,
        thumbnail: selectedThumbnail,
        reportId: reportId,
        type: Number(type),
      },
      replace: true,
    });
  }, [
    savedVideo,
    kidId,
    mi,
    reportId,
    thumbnails,
    setSavedThumbnail,
    setSavedVideo,
    inputValue,
    selectedImageIndex,
    navigate,
    type,
    bboxes,
    t,
    setAbortController,
    startAnalysis,
  ]);

  useEffect(() => {
    if (!open) {
      setSelectedImageIndex(null);
      setInputValue({ impression: '', physical: '', behavior: '', isOnlyOne: '' });
      setKidCaracterAddDialogOpen(false);
    }
  }, [open]);

  return (
    <Drawer
      open={open}
      anchor="bottom"
      onClose={(event: any, reason: any) => {
        if (reason === 'backdropClick') return;
        if (onClose) (onClose as any)(event, reason);
      }}
      PaperProps={{ sx: { width: '100vw', height: '100vh' } }}
    >
      <div className="w-full h-full p-5 min-h-[180px] text-[#1C1C1C] text-[20px] font-[700] font-['Pretendard'] flex flex-col items-center justify-between bg-white overflow-hidden">
        <div className="w-full">
          <div className="w-full text-center">{t('upload.select_rep_image')}</div>
          <button type="button" className="absolute top-[20px] right-[20px]" onClick={onClose}>
            <img src={IcClose} alt="Close" />
          </button>
          <div className="text-[24px] font-[700] min-h-[100px] h-auto text-center py-[30px]">
            {t('upload.start_diagnosis_question_line1')}
            <br />
            {t('upload.start_diagnosis_question_line2')}
          </div>
          <div className="w-full overflow-x-auto grid grid-cols-[1fr_1fr] items-center justify-start gap-[8px] text-[#303133] pb-2 mb-[24px]">
            {thumbnails?.length >= 1 &&
              thumbnails?.map((img, idx) => {
                return (
                  <button
                    key={String(idx)}
                    type="button"
                    onClick={() => setSelectedImageIndex(idx)}
                    className="relative overflow-hidden flex flex-col items-center justify-center gap-2 rounded-[10px] h-[156px] shrink-0"
                  >
                    <img loading="lazy" src={img} style={{ objectFit: 'cover' }} />
                    {selectedImageIndex === idx && (
                      <div className="w-full h-full absolute top-0 left-0 bg-black opacity-50" />
                    )}
                    {selectedImageIndex === idx && (
                      <img
                        src={IcCheckMintOn}
                        alt="Selected"
                        className="absolute top-[10px] right-[10px] w-[24px] h-[24px]"
                      />
                    )}
                  </button>
                );
              })}
          </div>
          <div className="w-full text-[14px] font-[400] text-[#363636] mb-3">
            {t('upload.rep_image_guide')}
          </div>
        </div>
        <div className="w-full flex justify-between gap-2">
          <Button variant="outlined" className="grow" onClick={onClose} sx={{ px: 3 }}>
            {t('upload.retake')}
          </Button>
          <Button
            variant="contained"
            className="grow"
            onClick={() => setKidCaracterAddDialogOpen(true)}
            disabled={selectedImageIndex === null}
          >
            {t('upload.start_diagnosis')}
          </Button>
        </div>
      </div>
      <KidCaracterAddDialog
        open={kidCaracterAddDialogOpen}
        onClose={() => setKidCaracterAddDialogOpen(false)}
        inputValue={inputValue}
        setInputValue={setInputValue}
        onSubmit={handleStartAnalysis}
      />
    </Drawer>
  );
}

export default RepImageSelectDialog;
